/*
 * Draft 7 shapes: routes, capacity, time off, Best order, day texts (schedule extras); On my way, crew locations, Loose
 * ends, visit reports (the field); hiring, person files and timesheets (HR).
 */
import { z } from "zod";
import { clientLook } from "./client-look.js";
import { FILE_KINDS } from "./clients.zod.js";

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-11-03.");
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time like 09:00.");

// Routes --------------------------------------------------------------------------------------------------------------

export const routeInput = z
  .object({
    name: z.string().trim().min(1, "Name the route.").max(60),
    weekday: z.number().int().min(1).max(7),
    crew: z.array(z.uuid()).max(10).default([]),
    color: z
      .string()
      .regex(/^#[0-9a-f]{6}$/i)
      .nullable()
      .optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type RouteInput = z.infer<typeof routeInput>;

export const routeRow = z.object({
  id: z.uuid(),
  name: z.string(),
  weekday: z.number(),
  crew: z.array(z.uuid()),
  color: z.string().nullable(),
  active: z.boolean(),
  /** Routes that take turns: the week (0 or 1, see weekParity) this route runs, or null for every week. */
  week: z.union([z.literal(0), z.literal(1)]).nullable(),
  jobs: z.array(
    z.object({ id: z.uuid(), name: z.string(), clientName: z.string(), address: z.string().nullable(), position: z.number() }),
  ),
});
export type RouteRow = z.infer<typeof routeRow>;

/** The route's jobs in their new order (every job on the route, once). Adding a job puts it on the route. */
/** date: the day it was reordered on; on a day where routes take turns a weekly job keeps the place for that week only. */
export const routeOrderBody = z.object({ jobIds: z.array(z.uuid()).max(300), date: day.optional() }).strict();

/**
 * Move a route's visits from one day to another, this time only (the route's regular day stays). A text goes to its
 * clients only when message is set.
 */
export const routeMoveBody = z
  .object({ from: day, to: day, message: z.string().trim().min(1).max(600).nullable().default(null) })
  .strict();
export const routeMoveAnswer = z.object({ moved: z.number(), texted: z.number() });

// Capacity and time off -----------------------------------------------------------------------------------------------

export const capacityDay = z.object({
  date: z.string(),
  teams: z.number(),
  /** People working after time off. */
  people: z.number(),
  hoursHeld: z.number(),
  hoursBooked: z.number(),
  visits: z.number(),
  open: z.boolean(),
  /** Room left in the morning (8 to 12) and in the afternoon (12 to 5), for booking from a quote. */
  morning: z.boolean(),
  afternoon: z.boolean(),
});
export type CapacityDay = z.infer<typeof capacityDay>;
export const capacityAnswer = z.object({ on: z.boolean(), days: z.array(capacityDay) });

const timeOffDays = {
  fromDay: day,
  toDay: day,
  fromTime: hhmm.nullable().optional(),
  toTime: hhmm.nullable().optional(),
  reason: z.string().trim().max(300).nullable().optional(),
};
const daysInOrder = (t: { fromDay: string; toDay: string }) => t.toDay >= t.fromDay;
const timesInOrder = (t: { fromTime?: string | null | undefined; toTime?: string | null | undefined }) =>
  !t.fromTime || !t.toTime || t.toTime > t.fromTime;
export const timeOffInput = z
  .object({ personId: z.uuid(), ...timeOffDays })
  .strict()
  .refine(daysInOrder, { message: "The last day can't be before the first.", path: ["toDay"] })
  .refine(timesInOrder, { message: "End after the start.", path: ["toTime"] });
/** A crew member marking their own day. */
export const myTimeOffInput = z
  .object(timeOffDays)
  .strict()
  .refine(daysInOrder, { message: "The last day can't be before the first.", path: ["toDay"] })
  .refine(timesInOrder, { message: "End after the start.", path: ["toTime"] });
export type TimeOffInput = z.infer<typeof timeOffInput>;

export const timeOffRow = z.object({
  id: z.uuid(),
  personId: z.uuid(),
  personName: z.string(),
  fromDay: z.string(),
  toDay: z.string(),
  fromTime: z.string().nullable(),
  toTime: z.string().nullable(),
  reason: z.string().nullable(),
});
export type TimeOffRow = z.infer<typeof timeOffRow>;

// Best order and day texts ---------------------------------------------------------------------------------------------

export const bestOrderBody = z.object({ date: day, refs: z.array(z.string().max(80)).min(2).max(25) }).strict();
export const bestOrderAnswer = z.object({
  order: z.array(z.string()),
  now: z.object({ minutes: z.number(), miles: z.number() }),
  best: z.object({ minutes: z.number(), miles: z.number() }),
  savedMinutes: z.number(),
  /** No Google key or no start address: a rough guess by straight lines at 30 mph, not real drive times. */
  estimate: z.boolean(),
});
export type BestOrderAnswer = z.infer<typeof bestOrderAnswer>;
/** A work truck pulling a trailer gets about this many miles a gallon, for the gas a shorter order saves. */
export const TRUCK_MPG = 15;
/** Gallons of gas for this many miles, to one decimal. */
export const gallonsFor = (miles: number) => Math.round((Math.max(0, miles) / TRUCK_MPG) * 10) / 10;
/** The line a route's stops make on the map, in this order. */
export const routePathBody = z.object({ date: day, refs: z.array(z.string().max(80)).min(2).max(80) }).strict();
export const routePathAnswer = z.object({
  points: z.array(z.tuple([z.number(), z.number()])),
  minutes: z.number(),
  miles: z.number(),
  /** True when the line follows the roads; false for straight lines (no Google key, or Google could not find it). */
  roads: z.boolean(),
  /** Where the teams start, when the line leaves from there and comes back (Google and a start address only). */
  home: z.tuple([z.number(), z.number()]).nullable(),
});
export type RoutePathAnswer = z.infer<typeof routePathAnswer>;
/** Finds map spots for a day's visits whose address has none yet, and saves them on the property. */
export const mapSpotsBody = z.object({ date: day }).strict();
export const mapSpotsAnswer = z.object({
  /** Addresses found and saved. */
  found: z.number(),
  /** Addresses Google could not find. */
  notFound: z.number(),
  /** Plain words when Google could not be asked (no key, key turned down), else null. */
  problem: z.string().nullable(),
});
export type MapSpotsAnswer = z.infer<typeof mapSpotsAnswer>;
/** Saves an order for the day's stops (from Best order or dragged by hand). */
export const dayOrderBody = z.object({ date: day, refs: z.array(z.string().max(80)).min(1).max(100) }).strict();

export const dayTextBody = z
  .object({
    date: day,
    routeId: z.uuid().nullable().optional(),
    /** Fill-ins: {first}, {service}, {day}, {date}, {address}. */
    message: z.string().trim().min(1, "Type the message.").max(600),
    /** Who would get it and who is skipped, nothing sent. */
    dry: z.boolean().default(false),
    /** Clients already told, left out. */
    leaveOut: z.array(z.uuid()).max(300).default([]),
    /** Their day-before reminder for that day is not sent (this text tells them). */
    skipReminders: z.boolean().default(true),
  })
  .strict();
export type DayTextBody = z.infer<typeof dayTextBody>;
export const dayTextAnswer = z.object({
  sent: z.number(),
  skipped: z.array(z.object({ clientId: z.uuid(), name: z.string(), why: z.string() })),
  samples: z.array(z.object({ name: z.string(), to: z.string(), text: z.string() })),
});
export type DayTextAnswer = z.infer<typeof dayTextAnswer>;

// The field -----------------------------------------------------------------------------------------------------------

export const onMyWayBody = z.object({ minutes: z.number().int().min(1).max(240).optional() }).strict();
export const onMyWayAnswer = z.object({ minutes: z.number(), text: z.string() });

export const locationBody = z
  .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    accuracyM: z.number().int().min(0).max(100_000).optional(),
  })
  .strict();

export const teamSpot = z.object({
  personId: z.uuid(),
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  accuracyM: z.number().nullable(),
  at: z.string(),
});
export type TeamSpot = z.infer<typeof teamSpot>;

export const LOOSE_END_KINDS = [
  "to_check",
  "past_due",
  "no_crew",
  "unsigned_quotes",
  "failed_charges",
  "expiring_files",
  "complaints",
  "new_applicants",
  "still_clocked_in",
] as const;
export type LooseEndKind = (typeof LOOSE_END_KINDS)[number];

export const looseEnd = z.object({
  key: z.string(),
  kind: z.enum(LOOSE_END_KINDS),
  title: z.string(),
  detail: z.string(),
  count: z.number(),
  /** Where to fix it, as an app path. */
  to: z.string(),
});
export type LooseEnd = z.infer<typeof looseEnd>;
export const snoozeBody = z.object({ key: z.string().min(1).max(200), until: day }).strict();

export const visitReportPublic = z.object({
  business: z.string(),
  brandColor: z.string().nullable(),
  /** The brand's colors and white logo for the top band (client-look.ts). */
  look: clientLook.optional(),
  firstName: z.string(),
  job: z.string(),
  date: z.string(),
  crew: z.array(z.string()),
  done: z.array(z.object({ room: z.string().nullable(), label: z.string() })),
  note: z.string().nullable(),
});
export type VisitReportPublic = z.infer<typeof visitReportPublic>;

// HR ------------------------------------------------------------------------------------------------------------------

export const hireBody = z
  .object({
    /** Send the team invite by text right away. */
    invite: z.boolean().default(false),
    role: z.enum(["crew", "dispatch", "manager"]).default("crew"),
    /** How they are paid, asked on Hire (question 80): a percent of each job or by the hour. */
    payType: z.enum(["percent", "hourly"]).default("percent"),
    percent: z.number().min(0).max(100).nullable().default(null),
    hourlyRate: z.number().min(0).max(500).nullable().default(null),
  })
  .strict();

export const personFileUpload = z
  .object({
    name: z.string().trim().min(1).max(200),
    mime: z.string().regex(/^[\w.+-]+\/[\w.+-]+$/, "That is not a file type."),
    kind: z.enum(FILE_KINDS).default("other"),
    dataBase64: z.string().max(28_000_000, "That file is too big. The most is 20 MB."),
    expiresOn: day.nullable().optional(),
  })
  .strict();
export const personFileRow = z.object({
  id: z.uuid(),
  name: z.string(),
  mime: z.string(),
  size: z.number(),
  kind: z.enum(FILE_KINDS),
  expiresOn: z.string().nullable(),
  createdAt: z.string(),
});
export type PersonFileRow = z.infer<typeof personFileRow>;

export const timesheetRow = z.object({
  personId: z.uuid(),
  name: z.string(),
  days: z.array(z.object({ date: z.string(), minutes: z.number() })),
  totalMinutes: z.number(),
});
export type TimesheetRow = z.infer<typeof timesheetRow>;

// The bell (draft 15, old inbox bell) ------------------------------------------------------------------------------

export const ALERT_KINDS = [
  "request",
  "quote_approved",
  "quote_opened",
  "payment",
  "job_added",
  "job_moved",
  "job_cancelled",
  "books",
  "signin",
  "check_late",
  "time_off",
  "visit_addons",
] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

export const alertRow = z.object({
  id: z.uuid(),
  kind: z.enum(ALERT_KINDS),
  title: z.string(),
  body: z.string().nullable(),
  /** Where it opens, as an app path. */
  link: z.string().nullable(),
  read: z.boolean(),
  at: z.string(),
});
export type AlertRow = z.infer<typeof alertRow>;

export const alertsAnswer = z.object({ alerts: z.array(alertRow), unread: z.number() });
export type AlertsAnswer = z.infer<typeof alertsAnswer>;

/** Mark these alerts read, or all of them when ids is left out. */
export const alertsReadBody = z.object({ ids: z.array(z.uuid()).max(100).optional() }).strict();

// Review page ---------------------------------------------------------------------------------------------------------

/** A client's own review page (/review/<token>), from the review request. */
export const reviewPagePublic = z.object({
  business: z.string(),
  brandColor: z.string().nullable(),
  /** The brand's colors and white logo for the top band (client-look.ts). */
  look: clientLook.optional(),
  firstName: z.string(),
  /** What was done and when ("deep clean", 2026-11-01), for the page to say whose visit it was. */
  job: z.string().nullable().optional(),
  day: z.string().nullable().optional(),
  /** Who did it, as the review text names them (an owner only when they worked alone); photo is a picture to show. */
  crew: z.array(z.object({ id: z.string(), first: z.string(), photo: z.boolean() })).optional(),
  /** The brand's own Google rating, as proof on the page. */
  google: z.object({ rating: z.number(), reviews: z.number() }).nullable().optional(),
  trade: z.string().optional(),
});
export type ReviewPagePublic = z.infer<typeof reviewPagePublic>;
export const reviewRateBody = z.object({
  stars: z.number().int().min(1).max(5),
  note: z.string().trim().max(2000).optional(),
});
/** go: the Google review link for 4 or 5 stars (null under 4, or when the business has none set). */
export const reviewRateAnswer = z.object({ go: z.string().nullable() });
export type ReviewRateAnswer = z.infer<typeof reviewRateAnswer>;
